import { useState } from 'react';
import { staffRouteAllowed } from '../lib/staff-access.js';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { i18n } from '@wms/shared';
import { useAuth } from '../lib/auth-context.js';
import logoUrl from '../assets/kraustymelis-logo.jpg';

const { t } = i18n;

/** Internal staff — tenant-wide reach across both modules (TRS §4.1). */
const INTERNAL_ROLES = ['ADMIN', 'OPERATOR', 'ACCOUNTANT'] as const;
const PARTNER_ROLES = ['PARTNER_ADMIN', 'ORDER_PLACER'] as const;

interface NavItem {
  to?: string;
  key: string;
  /** When set, only users holding one of these roles see the link. */
  roles?: readonly string[];
}

// Each link names exactly the roles it applies to, so a tier user only sees
// menu entries they can actually open (no more click-through 403s). Tier users
// get a Home link back to their `/my` landing; internal staff land on
// `/customers` and navigate from there.
const NAV: NavItem[] = [
  { to: '/my', key: 'nav.home', roles: [...PARTNER_ROLES, 'CUSTOMER'] },
  { to: '/customers', key: 'nav.customers', roles: INTERNAL_ROLES },
  { to: '/partners', key: 'nav.partners', roles: INTERNAL_ROLES },
  { to: '/orders', key: 'nav.orders', roles: [...INTERNAL_ROLES, ...PARTNER_ROLES] },
  { key: 'nav.warehouse', roles: [...INTERNAL_ROLES, ...PARTNER_ROLES, 'CUSTOMER'] },
  // Shared landing for personal settings and role-appropriate admin tools.
  { to: '/admin', key: 'nav.admin' },
];

/**
 * Whether a tier user's role may open `pathname`. Internal staff are
 * unrestricted. Tier users (Customer / Partner) get an allow-list matching
 * their nav + the sub-routes their pages link to; anything else renders the
 * friendly "service not applicable" panel instead of a raw 403 from the API.
 */
function isRouteAllowed(userRoles: readonly string[], pathname: string): boolean {
  if (INTERNAL_ROLES.some((r) => userRoles.includes(r))) return true;

  // Shared landing — index redirects here too.
  if (pathname === '/' || pathname === '/my') return true;

  // Appearance settings are cosmetic and available to every signed-in role.
  if (pathname === '/settings/appearance') return true;
  if (pathname === '/admin') return true;

  const isCustomer = userRoles.includes('CUSTOMER');
  const isPartner = PARTNER_ROLES.some((r) => userRoles.includes(r));

  if (isCustomer) {
    if (pathname === '/warehouse') return true;
    if (/^\/pallets\/[^/]+$/.test(pathname)) return true;
  }
  if (isPartner) {
    if (pathname === '/settings/notifications') return true;
    if (pathname === '/my/pallets') return true;
    if (pathname === '/stock' || pathname === '/shelf' || pathname === '/reports') return true;
    if (pathname === '/orders' || pathname === '/orders/new') return true;
    if (/^\/orders\/[^/]+$/.test(pathname)) return true;
    if (/^\/partners\/[^/]+\/low-stock$/.test(pathname)) return true;
    if (/^\/partners\/[^/]+\/skus\/[^/]+$/.test(pathname)) return true;
  }
  return false;
}

export function AppShell() {
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  const userRoles = user?.roles.map((r) => r.role) ?? [];
  const visibleNav = NAV.filter((item) => {
    if (item.roles && !item.roles.some((role) => userRoles.includes(role))) return false;
    if (item.to && !staffRouteAllowed(user, item.to)) return false;
    return true;
  });
  const routeAllowed =
    isRouteAllowed(userRoles, location.pathname) && staffRouteAllowed(user, location.pathname);
  const warehouseLinks: NavItem[] = [
    { to: '/stock', key: 'nav.stock', roles: [...INTERNAL_ROLES, ...PARTNER_ROLES] },
    { to: isPartnerUser(userRoles) ? '/my/pallets' : '/warehouse', key: 'nav.pallets' },
    { to: '/shelf', key: 'nav.shelf', roles: [...INTERNAL_ROLES, ...PARTNER_ROLES] },
  ].filter((item) => !item.roles || item.roles.some((role) => userRoles.includes(role)));

  // Close the drawer when the route changes — clicking a NavLink should hide
  // it on mobile without us having to wire `onClick` on every link.
  // (useLocation is stable; effect-free comparison via a key on the drawer.)
  void location.pathname;

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `rounded px-2 py-1 ${
      isActive
        ? 'bg-muted font-medium'
        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
    }`;

  const renderNavItem = (item: NavItem, mobile: boolean) => {
    if (item.to) {
      return (
        <NavLink key={item.to} to={item.to} className={linkClass}>
          {t(item.key)}
        </NavLink>
      );
    }
    return (
      <details key={`warehouse-${location.pathname}`} className="relative group">
        <summary className="cursor-pointer list-none rounded px-2 py-1 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
          {t('nav.warehouse')} <span aria-hidden="true">▾</span>
        </summary>
        <div
          className={`${mobile ? 'ml-3 mt-1 flex' : 'absolute left-0 top-full z-30 mt-1 flex min-w-36 border border-border bg-background p-1 shadow-lg'} flex-col gap-1 rounded text-sm`}
        >
          {warehouseLinks.map((link) => (
            <NavLink key={link.to} to={link.to!} className={linkClass}>
              {t(link.key)}
            </NavLink>
          ))}
        </div>
      </details>
    );
  };

  return (
    <div className="min-h-screen flex flex-col">
      <a href="#main-content" className="skip-link">
        {t('a11y.skip_to_content')}
      </a>
      <header className="border-b border-border px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <h1 className="flex items-center gap-2 font-semibold text-base">
            <img src={logoUrl} alt="" className="h-7 w-7 rounded object-contain" />
            <span>{t('app.name')}</span>
          </h1>

          {/* Desktop nav — visible at md+ */}
          <nav
            aria-label={t('a11y.primary_nav')}
            className="hidden md:flex items-center gap-1 text-sm"
          >
            {visibleNav.map((item) => renderNavItem(item, false))}
          </nav>

          <div className="hidden md:flex items-center gap-3 text-sm">
            {user ? (
              <>
                <span className="text-muted-foreground">{user.displayName}</span>
                <button
                  type="button"
                  onClick={() => void logout()}
                  className="rounded border border-border px-2 py-1 text-xs hover:bg-muted"
                >
                  {t('common.logout')}
                </button>
              </>
            ) : null}
          </div>

          {/* Mobile hamburger — visible below md */}
          <button
            type="button"
            className="md:hidden inline-flex items-center justify-center rounded border border-border h-9 w-9 text-base"
            aria-label={t('a11y.toggle_menu')}
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
            onClick={() => setMenuOpen((v) => !v)}
          >
            {menuOpen ? '✕' : '☰'}
          </button>
        </div>

        {/* Mobile drawer — keyed by pathname so a route change collapses it */}
        <nav
          id="mobile-nav"
          key={location.pathname}
          aria-label={t('a11y.primary_nav')}
          className={`md:hidden ${menuOpen ? 'mt-3 flex' : 'hidden'} flex-col gap-1 text-sm`}
          onClick={(event) => {
            if ((event.target as HTMLElement).closest('a')) setMenuOpen(false);
          }}
        >
          {visibleNav.map((item) => renderNavItem(item, true))}
          {user ? (
            <div className="mt-2 pt-2 border-t border-border flex items-center justify-between text-xs">
              <span className="text-muted-foreground">{user.displayName}</span>
              <button
                type="button"
                onClick={() => void logout()}
                className="rounded border border-border px-2 py-1 hover:bg-muted"
              >
                {t('common.logout')}
              </button>
            </div>
          ) : null}
        </nav>
      </header>
      <main id="main-content" className="flex-1 px-4 py-6" tabIndex={-1}>
        {routeAllowed ? <Outlet /> : <NotApplicable />}
      </main>
      <footer className="border-t border-border px-4 py-2 text-xs text-muted-foreground">
        {t('app.name')}
      </footer>
    </div>
  );
}

function isPartnerUser(roles: readonly string[]): boolean {
  return (
    !INTERNAL_ROLES.some((role) => roles.includes(role)) &&
    PARTNER_ROLES.some((role) => roles.includes(role))
  );
}

/**
 * Shown when a tier user lands on a route their role can't use (e.g. via a
 * bookmark or the browser back button). A calm "service not applicable"
 * message with a way back home — never a raw 403.
 */
function NotApplicable() {
  return (
    <section className="mx-auto max-w-md text-center space-y-3 py-12">
      <h2 className="text-lg font-semibold">{t('access.not_applicable.title')}</h2>
      <p className="text-sm text-muted-foreground">{t('access.not_applicable.body')}</p>
      <NavLink
        to="/my"
        className="inline-block rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
      >
        {t('access.back_home')}
      </NavLink>
    </section>
  );
}
